<template>
  <div class="card-team">
    <!-- Image -->
    <div
      v-if="image"
      class="card-team__image">
      <img
        :src="image"
        :alt="imageAlt"
        class="card-team__img" />
    </div>
    <div
      v-else
      class="card-team__image">
      <img
        src="~/assets/images/team-avatar.jpg"
        :alt="capitalizedName"
        class="card-team__img" />
    </div>

    <!-- BoldText with space-xs padding -->
    <div class="card-team__name">
      <bold-text
        :text="capitalizedName"
        color="red" />
    </div>

    <!-- 1px grey divider -->
    <div class="card-team__divider"></div>

    <!-- Role text with space-xs padding -->
    <div class="card-team__role">
      <p class="card-team__role-text">
        {{ role }}
      </p>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue'

  import BoldText from '../BoldText.vue'

  // Props
  const props = defineProps({
    image: {
      type: String,
      required: false,
      default: null,
      validator: value => value === null || (typeof value === 'string' && value.trim().length > 0)
    },
    imageAlt: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      required: true,
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    role: {
      type: String,
      required: true,
      validator: value => typeof value === 'string' && value.trim().length > 0
    }
  })

  // Computed property to capitalize first letter of each word
  const capitalizedName = computed(() => {
    return props.name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  })
</script>

<style lang="scss" scoped>
.card-team {
  background-color: var(--color-white);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-xs); // 16px gap from Figma design
  padding: 0 0 var(--space-xs);
  overflow: hidden;

  @include mq($until: tablet) {
    width: 100%;
  }

  &__image {
    width: 100%;
    aspect-ratio: 3 / 4; // tutte le foto alla stessa altezza → barra allineata
    overflow: hidden;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__name {
    padding: 0 var(--space-xs);
    text-align: center;
    // Altezza uniforme calcolata via JS (max tra le card) → la barra cade
    // allo stesso punto anche con nomi su più righe. Il nome resta ancorato
    // in basso vicino alla barra; lo spazio extra finisce sopra.
    min-height: var(--card-name-min-height, auto);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  &__divider {
    width: 100%;
    height: 1px;
    background-color: var(--color-grey);
  }

  &__role {
    padding: 0 var(--space-xs);

    @include mq($until: tablet) {
      width: calc(100% - calc(var(--space-xs) * 2));
    }
  }

  &__role-text {
    color: var(--color-black);
    font-size: var(--text-b-n, 16px);
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin: 0;
    text-align: center;
  }
}
</style>
